import AddIcon from '@mui/icons-material/Add'
import ApartmentIcon from '@mui/icons-material/Apartment'
import BuildIcon from '@mui/icons-material/Build'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import LinkIcon from '@mui/icons-material/Link'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import TimelineIcon from '@mui/icons-material/Timeline'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  TextField,
  IconButton,
  InputAdornment
} from '@mui/material'
import { Expense, ExpenseCategory, Vehicle } from '@otw/models'
import { formatPrice } from 'core/utils/inventoryUtils'
import dayjs from 'dayjs'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { useDashboardHomeVehicles } from 'hooks/dashboard'
import { useExpenses } from 'hooks/useExpenses'
import React, { useMemo, useState } from 'react'
import ExpenseDialog from '@/views/SuperAdmin/SuperAdminHome/components/ExpenseDialog'
import { ExpenseList } from './ExpenseList'
import SearchIcon from '@mui/icons-material/Search'

// Define EnrichedExpense type here
interface EnrichedExpense extends Expense {
  linkedVehicleData?: Vehicle | null;
}

interface ExpenseSummaryCardProps {
  dateRange: {
    start: Date
    end: Date
  }
}

export const ExpenseSummaryCard: React.FC<ExpenseSummaryCardProps> = ({ dateRange }) => {
  const { expenses, isLoading, totalExpensesByCategory, totalExpenses, addExpense, deleteExpense, updateExpense } = useExpenses({
    dateRange,
    options: { skipInventory: true },
  })
  const [openExpenseDialog, setOpenExpenseDialog] = useState(false)
  const [showExpenseList, setShowExpenseList] = useState(false)
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [openFullscreenDialog, setOpenFullscreenDialog] = useState(false)
  const isMobile = useIsMobile()
  const theme = useTheme()
  const { vehicles, isLoading: vehiclesLoading } = useDashboardHomeVehicles(dateRange)

  // Count linked vehicles for each expense category
  const getLinkedVehicleCount = (category: ExpenseCategory) => {
    return expenses.filter(expense => expense.category === category && expense.vehicleId !== null).length
  }

  // Calculate previous month date range for comparison
  const previousMonthDateRange = useMemo(() => {
    const currentStart = dayjs(dateRange.start)
    const currentEnd = dayjs(dateRange.end)
    
    // Calculate the previous period with the same duration
    const durationInDays = currentEnd.diff(currentStart, 'day')
    const previousEnd = currentStart.subtract(1, 'day')
    const previousStart = previousEnd.subtract(durationInDays, 'day')
    
    // Convert to Date objects as required by the useExpenses hook
    return {
      start: previousStart.toDate(),
      end: previousEnd.toDate()
    }
  }, [dateRange])

  // Get previous month expenses for comparison
  const { expenses: previousPeriodExpenses, totalExpensesByCategory: previousTotalByCategory, totalExpenses: previousTotalExpenses } = useExpenses({
    dateRange: previousMonthDateRange,
    options: { dontShowUpdates: true, skipInventory: true },
  })

  // Calculate month-over-month changes
  const calculateChangePercentage = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0
    return ((current - previous) / previous) * 100
  }

  // Group expenses by categories for chart
  const expenseCategories = [
    {
      name: 'Shipping',
      value: totalExpensesByCategory.Shipping,
      previousValue: previousTotalByCategory.Shipping,
      icon: <LocalShippingIcon />,
      linkedVehicles: getLinkedVehicleCount('Shipping'),
      change: calculateChangePercentage(totalExpensesByCategory.Shipping, previousTotalByCategory.Shipping)
    },
    {
      name: 'Repairs',
      value: totalExpensesByCategory.Repairs,
      previousValue: previousTotalByCategory.Repairs,
      icon: <BuildIcon />,
      linkedVehicles: getLinkedVehicleCount('Repairs'),
      change: calculateChangePercentage(totalExpensesByCategory.Repairs, previousTotalByCategory.Repairs)
    },
    {
      name: 'Rental Reimbursement',
      value: totalExpensesByCategory.RentalReimbursement,
      previousValue: previousTotalByCategory.RentalReimbursement,
      icon: <DirectionsCarIcon />,
      linkedVehicles: getLinkedVehicleCount('RentalReimbursement'),
      change: calculateChangePercentage(totalExpensesByCategory.RentalReimbursement, previousTotalByCategory.RentalReimbursement)
    },
    {
      name: 'Storage Fees',
      value: totalExpensesByCategory.StorageFees,
      previousValue: previousTotalByCategory.StorageFees,
      icon: <WarehouseIcon />,
      linkedVehicles: getLinkedVehicleCount('StorageFees'),
      change: calculateChangePercentage(totalExpensesByCategory.StorageFees, previousTotalByCategory.StorageFees)
    },
    {
      name: 'Other',
      value: totalExpensesByCategory.Other,
      previousValue: previousTotalByCategory.Other,
      icon: <ApartmentIcon />,
      linkedVehicles: getLinkedVehicleCount('Other'),
      change: calculateChangePercentage(totalExpensesByCategory.Other, previousTotalByCategory.Other)
    },
  ]

  // Calculate total change percentage
  const totalChangePercentage = calculateChangePercentage(totalExpenses, previousTotalExpenses)

  // Enrich expenses with vehicle data
  const enrichedExpenses = useMemo<EnrichedExpense[]>(() => {
    if (vehiclesLoading || !vehicles) return expenses.map(exp => ({ ...exp, linkedVehicleData: null }));
    return expenses.map(expense => {
      let linkedVehicleData: Vehicle | null = null;
      if (expense.vehicleId) {
        linkedVehicleData = vehicles.find(v => v.id === expense.vehicleId) || null;
      }
      return { ...expense, linkedVehicleData };
    });
  }, [expenses, vehicles, vehiclesLoading]);

  const filteredExpenses = useMemo(() => {
    if (!showExpenseList || !searchQuery) return enrichedExpenses;
    const lowerCaseQuery = searchQuery.toLowerCase();
    return enrichedExpenses.filter(expense =>
      expense.description.toLowerCase().includes(lowerCaseQuery) ||
      expense.category.toLowerCase().includes(lowerCaseQuery) ||
      (expense.customCategory && expense.customCategory.toLowerCase().includes(lowerCaseQuery)) ||
      (expense.linkedVehicleData && (
        expense.linkedVehicleData.vin?.toLowerCase().includes(lowerCaseQuery) ||
        expense.linkedVehicleData.stock_number?.toLowerCase().includes(lowerCaseQuery) ||
        expense.linkedVehicleData.make?.toLowerCase().includes(lowerCaseQuery) ||
        expense.linkedVehicleData.model?.toLowerCase().includes(lowerCaseQuery) ||
        expense.linkedVehicleData.year?.toString().includes(lowerCaseQuery)
      ))
    );
  }, [enrichedExpenses, searchQuery, showExpenseList]);

  // Handle expense dialog open/close
  const handleOpenExpenseDialog = () => {
    setEditingExpense(null) // Reset editing expense
    setOpenExpenseDialog(true)
  }

  const handleOpenEditDialog = (expense: Expense) => {
    setEditingExpense(expense)
    setOpenExpenseDialog(true)
  }

  const handleCloseExpenseDialog = () => {
    setOpenExpenseDialog(false)
    setEditingExpense(null)
  }

  // Handle fullscreen dialog
  const handleOpenFullscreenDialog = () => {
    setOpenFullscreenDialog(true)
  }

  const handleCloseFullscreenDialog = () => {
    setOpenFullscreenDialog(false)
  }

  // Handle expense submission
  const handleSubmitExpense = async (
    expense: Omit<Expense, 'created_by' | 'created_at' | 'updated_at' | 'updated_by'>,
    attachmentFiles?: File[] | null,
  ) => {
    try {
      if (editingExpense) {
        // Update existing expense
        await updateExpense({ expenseData: expense as Expense, attachmentFiles })
      } else {
        // Add new expense - strip the id for new expenses
        const { id, ...newExpenseData } = expense
        await addExpense({
          expenseData: newExpenseData as Omit<Expense, 'created_by' | 'created_at' | 'updated_at' | 'updated_by'>,
          attachmentFiles,
        })
      }
      
      // Ensure the expense list is shown after adding/editing
      if (!showExpenseList) {
        setShowExpenseList(true)
      }
    } catch (error) {
      console.error('Error handling expense submission:', error)
    }
  }

  // Handle expense deletion
  const handleDeleteExpense = async (id: string): Promise<void> => {
    await deleteExpense(id)
    return
  }

  // Helper function to get change indicator
  const getChangeIndicator = (change: number) => {
    if (Math.abs(change) < 0.1) return null // No significant change
    
    if (change > 0) {
      return (
        <Tooltip title={`${change.toFixed(1)}% increase from previous period`}>
          <TrendingUpIcon 
            fontSize="small" 
            color="error" 
            sx={{ ml: 0.5, verticalAlign: 'middle' }}
          />
        </Tooltip>
      )
    } else {
      return (
        <Tooltip title={`${Math.abs(change).toFixed(1)}% decrease from previous period`}>
          <TrendingDownIcon 
            fontSize="small" 
            color="success"
            sx={{ ml: 0.5, verticalAlign: 'middle' }}
          />
        </Tooltip>
      )
    }
  }

  return (
    <Card elevation={3} sx={{ height: isMobile ? 'auto' : '100%' }}>
      <CardHeader
        title="Expense Summary"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenExpenseDialog}
            >
              {isMobile ? 'Add' : 'Add Expense'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowExpenseList(!showExpenseList)}
              startIcon={showExpenseList ? <CompareArrowsIcon /> : <TimelineIcon />}
            >
              {showExpenseList ? 'Show Summary' : 'Show Details'}
            </Button>
            <Tooltip title="Open in Full Screen">
              <IconButton onClick={handleOpenFullscreenDialog}>
                <FullscreenIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <Divider />
      <CardContent
        sx={{
          height: isMobile ? 'auto' : 'calc(100% - 73px)',
          maxHeight: isMobile ? 'auto' : showExpenseList ? 'calc(100vh - 300px)' : 350,
          overflow: 'auto',
        }}
      >
        {showExpenseList && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Search Expenses (Description, Category, VIN, Stock #, Make, Model, Year)"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
        {showExpenseList ? (
          <ExpenseList expenses={filteredExpenses} onDelete={handleDeleteExpense} onEdit={handleOpenEditDialog} />
        ) : isMobile ? (
          <Box sx={{ overflow: 'visible' }}>
            <Stack spacing={1.5}>
              {/* Summary Statistics for Mobile */}
              <Paper elevation={2} sx={{ p: 1.5, mb: 1, bgcolor: 'grey.100' }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Total Expenses</Typography>
                    <Typography variant="h6">{formatPrice(totalExpenses)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">vs Previous Period</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" color={totalChangePercentage > 0 ? 'error.main' : 'success.main'}>
                        {totalChangePercentage > 0 ? '+' : ''}{totalChangePercentage.toFixed(1)}%
                        {getChangeIndicator(totalChangePercentage)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Total Entries</Typography>
                    <Typography variant="body1">{expenses.length}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Linked to Vehicles</Typography>
                    <Typography variant="body1">
                      {expenses.filter(e => e.vehicleId).length} 
                      ({Math.round((expenses.filter(e => e.vehicleId).length / expenses.length) * 100) || 0}%)
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Category Breakdowns */}
              {expenseCategories.map(category => (
                <Paper key={category.name} elevation={1} sx={{ p: 1.5 }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {category.icon}
                      <Typography variant='subtitle2'>{category.name}</Typography>
                      {category.linkedVehicles > 0 && (
                        <Tooltip
                          title={`${category.linkedVehicles} expense${category.linkedVehicles > 1 ? 's' : ''} linked to vehicle${category.linkedVehicles > 1 ? 's' : ''}`}
                        >
                          <Badge badgeContent={category.linkedVehicles} color="primary" sx={{ ml: 0.5 }}>
                            <LinkIcon
                              fontSize='small'
                              sx={{
                                opacity: 0.6,
                                color: 'primary.main',
                              }}
                            />
                          </Badge>
                        </Tooltip>
                      )}
                    </Box>
                    <Box>
                      <Typography variant='body1' fontWeight='bold'>
                        {formatPrice(category.value)}
                        {getChangeIndicator(category.change)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" align="right" display="block">
                        vs {formatPrice(category.previousValue)}
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant='determinate'
                    value={totalExpenses > 0 ? (category.value / totalExpenses) * 100 : 0}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  />
                  <Typography variant='caption' color='text.secondary' align='right' display='block'>
                    {totalExpenses > 0 ? `${((category.value / totalExpenses) * 100).toFixed(1)}%` : '0%'}
                  </Typography>
                </Paper>
              ))}
              <Paper
                elevation={2}
                sx={{
                  p: 1.5,
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    Total
                  </Typography>
                  <Box>
                    <Typography variant='subtitle1' fontWeight='bold'>
                      {formatPrice(totalExpenses)}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', color: 'rgba(255,255,255,0.7)' }}>
                      vs {formatPrice(previousTotalExpenses)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Stack>
          </Box>
        ) : (
          <Box>
            {/* Desktop Summary Statistics */}
            <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">Total Expenses</Typography>
                  <Typography variant="h6">{formatPrice(totalExpenses)}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">vs Previous Period</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body1" color={totalChangePercentage > 0 ? 'error.main' : 'success.main'}>
                      {totalChangePercentage > 0 ? '+' : ''}{totalChangePercentage.toFixed(1)}%
                      {getChangeIndicator(totalChangePercentage)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">Total Entries</Typography>
                  <Typography variant="body1">{expenses.length}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">Linked to Vehicles</Typography>
                  <Typography variant="body1">
                    {expenses.filter(e => e.vehicleId).length} 
                    ({Math.round((expenses.filter(e => e.vehicleId).length / expenses.length) * 100) || 0}%)
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Desktop Category Table */}
            <TableContainer component={Box} sx={{ maxHeight: 300 }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align='right'>Amount</TableCell>
                    <TableCell align='right'>Previous</TableCell>
                    <TableCell align='right'>Change</TableCell>
                    <TableCell align='right'>% of Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenseCategories.map(category => (
                    <TableRow key={category.name}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {category.icon}
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {category.name}
                            {category.linkedVehicles > 0 && (
                              <Tooltip
                                title={`${category.linkedVehicles} expense${category.linkedVehicles > 1 ? 's' : ''} linked to vehicle${category.linkedVehicles > 1 ? 's' : ''}`}
                              >
                                <Badge badgeContent={category.linkedVehicles} color="primary" sx={{ ml: 1 }}>
                                  <LinkIcon
                                    fontSize='small'
                                    sx={{
                                      opacity: 0.6,
                                      color: 'primary.main',
                                    }}
                                  />
                                </Badge>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align='right'>{formatPrice(category.value)}</TableCell>
                      <TableCell align='right'>{formatPrice(category.previousValue)}</TableCell>
                      <TableCell align='right'>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Typography variant="body2" color={category.change > 0 ? 'error.main' : 'success.main'}>
                            {category.change > 0 ? '+' : ''}{category.change.toFixed(1)}%
                          </Typography>
                          {getChangeIndicator(category.change)}
                        </Box>
                      </TableCell>
                      <TableCell align='right'>
                        {totalExpenses > 0
                          ? `${((category.value / totalExpenses) * 100).toFixed(1)}%`
                          : '0%'}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'primary.light' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>Total</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                      {formatPrice(totalExpenses)}
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                      {formatPrice(previousTotalExpenses)}
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Typography variant="body2" sx={{ color: 'inherit' }}>
                          {totalChangePercentage > 0 ? '+' : ''}{totalChangePercentage.toFixed(1)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>

      {/* Use the separate ExpenseDialog component */}
      <ExpenseDialog 
        open={openExpenseDialog} 
        onClose={handleCloseExpenseDialog} 
        onSubmit={handleSubmitExpense} 
        editMode={!!editingExpense}
        expenseToEdit={editingExpense}
      />

      {/* Full-screen Dialog */}
      <Dialog
        open={openFullscreenDialog}
        onClose={handleCloseFullscreenDialog}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? '100vh' : '90vh',
            maxHeight: isMobile ? '100vh' : '90vh',
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Expense Summary - Full View</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenExpenseDialog}
                size="small"
              >
                Add Expenses
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowExpenseList(!showExpenseList)}
                startIcon={showExpenseList ? <CompareArrowsIcon /> : <TimelineIcon />}
                size="small"
              >
                {showExpenseList ? 'Show Summary' : 'Show Details'}
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', p: 2, overflow: 'auto' }}>
            {showExpenseList && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Search Expenses (Description, Category, VIN, Stock #, Make, Model, Year)"
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
            {showExpenseList ? (
              <ExpenseList expenses={filteredExpenses} onDelete={handleDeleteExpense} onEdit={handleOpenEditDialog} />
            ) : (
              <Box>
                {/* Summary Statistics */}
                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Total Expenses</Typography>
                      <Typography variant="h6">{formatPrice(totalExpenses)}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">vs Previous Period</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1" color={totalChangePercentage > 0 ? 'error.main' : 'success.main'}>
                          {totalChangePercentage > 0 ? '+' : ''}{totalChangePercentage.toFixed(1)}%
                          {getChangeIndicator(totalChangePercentage)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Total Entries</Typography>
                      <Typography variant="body1">{expenses.length}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Linked to Vehicles</Typography>
                      <Typography variant="body1">
                        {expenses.filter(e => e.vehicleId).length} 
                        ({Math.round((expenses.filter(e => e.vehicleId).length / expenses.length) * 100) || 0}%)
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Category Table */}
                <TableContainer component={Box} sx={{ maxHeight: 400 }}>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell align='right'>Amount</TableCell>
                        <TableCell align='right'>Previous</TableCell>
                        <TableCell align='right'>Change</TableCell>
                        <TableCell align='right'>% of Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expenseCategories.map(category => (
                        <TableRow key={category.name}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {category.icon}
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {category.name}
                                {category.linkedVehicles > 0 && (
                                  <Tooltip
                                    title={`${category.linkedVehicles} expense${category.linkedVehicles > 1 ? 's' : ''} linked to vehicle${category.linkedVehicles > 1 ? 's' : ''}`}
                                  >
                                    <Badge badgeContent={category.linkedVehicles} color="primary" sx={{ ml: 1 }}>
                                      <LinkIcon
                                        fontSize='small'
                                        sx={{
                                          opacity: 0.6,
                                          color: 'primary.main',
                                        }}
                                      />
                                    </Badge>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align='right'>{formatPrice(category.value)}</TableCell>
                          <TableCell align='right'>{formatPrice(category.previousValue)}</TableCell>
                          <TableCell align='right'>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                              <Typography variant="body2" color={category.change > 0 ? 'error.main' : 'success.main'}>
                                {category.change > 0 ? '+' : ''}{category.change.toFixed(1)}%
                              </Typography>
                              {getChangeIndicator(category.change)}
                            </Box>
                          </TableCell>
                          <TableCell align='right'>
                            {totalExpenses > 0
                              ? `${((category.value / totalExpenses) * 100).toFixed(1)}%`
                              : '0%'}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'primary.light' }}>
                        <TableCell sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>Total</TableCell>
                        <TableCell align='right' sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                          {formatPrice(totalExpenses)}
                        </TableCell>
                        <TableCell align='right' sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                          {formatPrice(previousTotalExpenses)}
                        </TableCell>
                        <TableCell align='right' sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Typography variant="body2" sx={{ color: 'inherit' }}>
                              {totalChangePercentage > 0 ? '+' : ''}{totalChangePercentage.toFixed(1)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align='right' sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>100%</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

export default ExpenseSummaryCard 